import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { CurrentUserService } from '../../user/services';
import { InitConfigurationDialogComponent } from '../../distribution/init-configuration-dialog/init-configuration-dialog.component';
import { InitConfiguration } from '../../distribution/models/init-configuration';
import { ModalDismissStatus } from '../../distribution/models/modal-dismiss-status';
import { DataFieldGroup } from '../../shared/datafields/models';
import { GenericModalService, ModalSettingsTemplate } from '../../shared/generic-modal/generic-modal.service';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { ArtifactAssemblyTypes, AssemblyArtifact } from '../../developer/models/artifact';
import { HttpHandlerEntity } from '../models/http-handler';
import { GridService } from '../../shared/grid/grid.service';
import { HttpHandlerService } from '../services/http-handler.service';
import { DeveloperArtifactService } from 'src/app/developer/services/developer-artifact.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule, DxTextBoxModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoColumnFixingModule, DxoEditingModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';

import { DeveloperArtifactAssemblyPickerComponent } from '../../developer/developer-artifacts/developer-artifact-assembly-picker.component';
import { DataFieldGroupContainerComponent } from '../../shared/datafields/datafield-group-container.component';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'app-http-handlers',
    templateUrl: './http-handlers.component.html',
    styleUrls: ['./http-handlers.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoColumnFixingModule, DxoEditingModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule, BizUserColumnDirective, BizDateColumnDirective, DxTemplateModule, DxTextBoxModule, DeveloperArtifactAssemblyPickerComponent, DataFieldGroupContainerComponent]
})
export class HttpHandlersComponent implements OnInit {

  hasCreatePerm = false;
    hasEditPerm = false;
    hasDeletePerm = false;
    allowShowLog = false;

    httpHandlers: HttpHandlerEntity[] = [];
    selectedHandlers: number[] = [];
    artifacts: AssemblyArtifact[] = [];
    currentHttpHandler: HttpHandlerEntity | null;

    httpHandlerDisplayGroups: DataFieldGroup[] = [];
    httpHandlerFormGroup: UntypedFormGroup = new UntypedFormGroup({});

    baseApiUrl: string;

    canExportHandlers = false;

    constructor(
        private httpHandlerService: HttpHandlerService,
        private toastService: ToastService,
        private currentUserService: CurrentUserService,
        private loadingPanelService: LoadingPanelService,
        private modalService: GenericModalService,
        private developerArtifactService: DeveloperArtifactService,
        public bizGridFiltervalues: BizGridFiltervalues,
        private gridService: GridService) {

        this.allowUpdatingRow = this.allowUpdatingRow.bind(this);
        this.allowDeletingRow = this.allowDeletingRow.bind(this);
        this.editInitConfig = this.editInitConfig.bind(this);
        this.displayExprArtifact = this.displayExprArtifact.bind(this);
    }

    async ngOnInit() {
        this.artifacts = await this.developerArtifactService.getAssemblyArtifacts(true, [ArtifactAssemblyTypes.TypeHttpHandler, ArtifactAssemblyTypes.TypeHttpHandler2]);
        this.hasCreatePerm = this.currentUserService.hasPermission('HttpHandler_Action_Create');
        this.hasEditPerm = this.currentUserService.hasPermission('HttpHandler_Action_Edit');
        this.hasDeletePerm = this.currentUserService.hasPermission('HttpHandler_Action_Delete');
        this.allowShowLog = this.currentUserService.hasPermission('HttpHandler_Action_ShowLog');
        this.baseApiUrl = await this.httpHandlerService.getBaseApiUrl();
        if (this.baseApiUrl.slice(-1) != "/") {
            this.baseApiUrl = this.baseApiUrl + "/";
        }
        this.refresh();
    }

    allowUpdatingRow(options: { component: any; row: any; }): boolean {
        const item: HttpHandlerEntity = options?.row?.data;
        if (item) {
            return this.hasEditPerm;
        }
        return false;
    }

    allowDeletingRow(options: { component: any; row: any; }): boolean {
        const item: HttpHandlerEntity = options?.row?.data;
        if (item) {
            return this.hasDeletePerm;
        }
        return false;
    }

    async editInitConfig(httpHandler: HttpHandlerEntity) {
        let initConfiguration = await this.httpHandlerService.getHttpHandlerInitConfig(httpHandler.Id);
        initConfiguration = initConfiguration ?? { FormatTypeId: null } as InitConfiguration;

        let componentValues = {
            initConfiguration: initConfiguration,
            headline: httpHandler.Name,
            showDelete: initConfiguration.Id > 0
        };

        this.modalService.openComponentType<InitConfiguration>(InitConfigurationDialogComponent, componentValues, new ModalSettingsTemplate('lg', 'static')).then(async result => {
            this.loadingPanelService.showLoadingPanelDirect('Saving init configuration...');
            initConfiguration = await this.httpHandlerService.saveHttpHandlerInitConfig(httpHandler.Id, result);
            httpHandler.InitConfigurationId = initConfiguration.Id; // The value is also stored on the http handler at the backend - so this is just to set the value here in the frontend.
            this.loadingPanelService.hideLoadingPanel();
        }).catch((reason: ModalDismissStatus) => {
            if (reason == ModalDismissStatus.Delete) { // If the delete button has been pressed...
                this.modalService.confirmDirect('Delete', `Delete init-config for ${httpHandler.Name}?`).then(async () => {
                    this.loadingPanelService.showLoadingPanelDirect('Deleting init configuration...');
                    await this.httpHandlerService.deleteHttpHandlerInitConfig(httpHandler.Id);
                    httpHandler.InitConfigurationId = null;
                    this.loadingPanelService.hideLoadingPanel();
                }).catch(() => { });
            }
        });
    }

    displayExprArtifact(e: any) {
        // TODO: Find en bedre måde at håndtere dette, så det ikke er nødvendigt at loade alle artifacts...
        if (this.artifacts) {
            var artifact = this.artifacts.find(a => a.ArtifactId == e.value);
            if (artifact) {
                return `${artifact.Name} [${artifact.ClassName}] [${artifact.Filename}]`;
            }
        }
        return "Unknown...";
    }

    async refresh() {
        this.loadingPanelService.showLoadingPanelDirect('Loading http handlers...');
        this.httpHandlers = await this.httpHandlerService.getHttpHandlers();
        this.httpHandlerDisplayGroups = [];
        this.currentHttpHandler = null;
        this.loadingPanelService.hideLoadingPanel();
    }

    onInitNewRow(e: any): void {
        e.data = { Id: null, Name: '', Description: '', BuildInSecurity: true } as HttpHandlerEntity;
    }

    onRowInserting = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as HttpHandlerEntity, item => this.saveHttpHandler(item));
    };

    onRowUpdating = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.key, ...e.newData } as HttpHandlerEntity, item => this.saveHttpHandler(item), e.newData);
    };

    onRowRemoving = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as HttpHandlerEntity, item => this.removeHttpHandler(item));
    };

    onSelectionChanged(e: any): void {
            this.selectedHandlers = e.selectedRowsData.map((item: HttpHandlerEntity) => item.Id);
            this.canExportHandlers = this.selectedHandlers.length > 0;
        }

    async saveHttpHandler(httpHandler: HttpHandlerEntity): Promise<HttpHandlerEntity> {
        const httpHandlerExist = this.httpHandlers.find(i => i.Id != httpHandler.Id && i.UrlKey == httpHandler.UrlKey);
        if (httpHandlerExist != null) {
            throw new Error(`Another http handler with that UrlKey already exists. Name of the existing http handler: ${httpHandlerExist.Name}`);
        }

        let datafieldValues = DataFieldGroup.getDataFieldValues(this.httpHandlerDisplayGroups, true);
        const newHttpHandler = await this.httpHandlerService.saveHttpHandler(httpHandler, datafieldValues);
        this.toastService.showToast('Http handler saved...', ToastType.success);
        return newHttpHandler;
    }

    async removeHttpHandler(httpHandler: HttpHandlerEntity): Promise<HttpHandlerEntity> {
        await this.httpHandlerService.deleteHttpHandler(httpHandler.Id);
        this.toastService.showToast('Http handler deleted...', ToastType.success);
        return httpHandler;
    }

    async onEditingStart(e: any) {
        const plugin = e.data as HttpHandlerEntity;
        this.currentHttpHandler = plugin;
        e.component.cellValue(e.component.getRowIndexByKey(e.key), "Name", e.component.cellValue(e.component.getRowIndexByKey(e.key), "Name")); // Lidt hacky, men for at vi kan få de dynamiske datafield-værdier med i en save, hvor man ikke har ændret andet end disse værdier, så trigger vi en ændring her, så update altid bliver kaldt.
    }

    async onEditCancelled(e: any) {
        this.currentHttpHandler = null;
        this.httpHandlerDisplayGroups = [];
    }

    async setArtifactId(artifactId: string, cellInfo: any) {
        cellInfo.setValue(artifactId || '');
        if (artifactId) {
            this.loadingPanelService.showLoadingPanelDirect('Loading config...');
            this.httpHandlerDisplayGroups = await this.httpHandlerService.getHttpHandlerDataFieldGroups(artifactId, this.currentHttpHandler?.Id);
            this.loadingPanelService.hideLoadingPanel();
        }
        else {
            this.httpHandlerDisplayGroups = [];
        }
    }

    async exportHandlers(handlers: number[]): Promise<void> {
        await this.httpHandlerService.exportHttpHandlers(handlers);
      }
}
